import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Background, Panel, useReactFlow, ConnectionMode, SelectionMode } from "@xyflow/react";
import { EMPTY_EDGES, NODE_WIDTH, PROCESS_FLOW_REGISTRY, buildNodeOverlayMap } from "../core";
import { buildSuggestionInfoMap } from "../core/builder";
import { isNoteNodeId, noteIdFromNodeId } from "../core/notes";
import { classifyCanvasNode, isBackingEntityKind } from "../core/nodeKind";
import { resolveSelection } from "../core/resolveSelection";
import { FlowCanvas, useEditableFlow, EditorModeContext, SnapConnectionLine, useHelperLines, useReleaseStuckModifiers, makeEdgeKey } from "../../flow-kit";
import { createProcessFlowEditorAdapter, PROCESS_FLOW_LANE_LAYOUT } from "../flow-adapters";
import { getHelperLineDimensions } from "../core/layout";
import ProcessFlowStepDetailSidebar from "../../step-detail/ProcessFlowStepDetailSidebar";
import { FlowViewportHandler } from "../viewportFit";
import { FloatingDetailPanel } from "./FloatingDetailPanel";
import { useEditorConnections } from "./hooks/useEditorConnections";
import { useNoteLayer } from "./hooks/useNoteLayer";
import { useEmptyStepPlaceholderLayer } from "./hooks/useEmptyStepPlaceholderLayer";
import { useDetailPanelReveal } from "./hooks/useDetailPanelReveal";
import { useEditorSelection } from "./useEditorSelection";
import { decorateEditorNode } from "./decorateEditorNode";
import { EditorActionsContext, EditorSelectionContext, EdgeCallbacksContext, EdgeWaypointActionsContext } from "./editorContexts";
// RF mouse-button ids for middle + right — used when `panOnDrag` should only
// pan on those buttons (leaving LMB free for rectangular selection).
const PAN_ON_MIDDLE_AND_RIGHT = [
    1,
    2
];
// A small process would otherwise open at up to the canvas maxZoom (200 %).
// Module-level so the options keep one identity across renders.
const INITIAL_FIT_VIEW_OPTIONS = {
    maxZoom: 1
};
export function ProcessFlowEditor({ process, nodePositions, onConnectSteps, onDisconnectSteps, onSavePositions, renderDetail, draftEvents, bottomToolbar, contextMenu, controlledSelectedNodeId, onSelectedNodeChange, revealRequest, onPaneContextMenu, onNodeContextMenu, onEdgeContextMenu, errorNodeIds, incompleteRuleIdsByStep, disconnectedEventIds, overlays, fitRequest, openAssignments, onOpenAssignmentClick, cursorMode = "pan", onDeleteNodes, swimlaneOrder, laneColors, onMoveSwimlaneUp, onMoveSwimlaneDown, edgeWaypoints, onUpdateEdgeLayouts, assigneeLookup, entitySuggestions, notes, onUpdateNote, onPlaceNote, onRetargetNote, onDeleteNote, onAddFirstStep }) {
    const { t } = useTranslation();
    const { handleConnect, handleConnectStart, handleConnectEnd } = useEditorConnections({
        onConnectSteps
    });
    // Canvas proposed-name/status map derived from the draft-wide suggestion set.
    // Memoized so the source memo below only churns on a real suggestion change.
    const suggestionInfoById = useMemo(()=>buildSuggestionInfoMap(entitySuggestions), [
        entitySuggestions
    ]);
    const overlayMap = useMemo(()=>buildNodeOverlayMap(overlays), [
        overlays
    ]);
    const adapter = useMemo(()=>createProcessFlowEditorAdapter({
            registry: PROCESS_FLOW_REGISTRY,
            handlers: {
                onSavePositions
            }
        }), [
        onSavePositions
    ]);
    const editableEdgesEnabled = Boolean(adapter.features?.editableEdges);
    // The empty-step placeholder hangs off the start event, so the editor shows
    // a blank start whenever it can offer the placeholder.
    const showBlankStartEvent = Boolean(onAddFirstStep);
    const source = useMemo(()=>({
            process,
            options: {
                openAssignments,
                swimlaneOrderOverride: swimlaneOrder,
                laneColorsOverride: laneColors,
                onMoveSwimlaneUp,
                onMoveSwimlaneDown,
                events: draftEvents,
                assigneeLookup,
                suggestionsById: suggestionInfoById,
                // Editor-only: a freshly added event floats until the user wires it.
                showUnattachedIntermediateEvents: true,
                showBlankStartEvent
            }
        }), [
        process,
        openAssignments,
        swimlaneOrder,
        laneColors,
        onMoveSwimlaneUp,
        onMoveSwimlaneDown,
        draftEvents,
        assigneeLookup,
        suggestionInfoById,
        showBlankStartEvent
    ]);
    // Decoration is baked into the hook's state layer so dimension updates from
    // `onNodesChange` preserve it. A new `decorate` identity re-runs positioning
    // and lane layout, so its dependencies are the marker props only.
    // Captured as a boolean so decorate only re-runs when the presence of a
    // handler flips (not on every parent re-render where the callback is fresh).
    const hasDetailRenderer = !!renderDetail;
    const decorate = useMemo(()=>{
        const markers = {
            errorNodeIds,
            incompleteRuleIdsByStep,
            disconnectedEventIds,
            overlayMap,
            hasDetailRenderer
        };
        return (node, ctx)=>decorateEditorNode(node, ctx, markers);
    }, [
        errorNodeIds,
        incompleteRuleIdsByStep,
        disconnectedEventIds,
        overlayMap,
        hasDetailRenderer
    ]);
    const { selectedNodeIds, selectedNodeId, handleDragEnd, registerDragEndEffects, handleSelectionChange, applyExternalSelectChanges, handleNodeClick, handlePaneClick, handleNodeDragStart: selectionOnDragStart, handleSelectionStart, handleSelectionEnd, closeDetailPanel } = useEditorSelection({
        controlledSelectedNodeId,
        onSelectedNodeChange,
        onOpenAssignmentClick,
        hasDetailRenderer
    });
    const { transformChanges, overlay: helperLinesOverlay, clearGuides } = useHelperLines({
        getDimensions: getHelperLineDimensions,
        isCandidate: (n)=>!PROCESS_FLOW_LANE_LAYOUT.isLane(n),
        // Clamp to NODE_WIDTH so small nodes (start/end/gateway, 60×60) still
        // surface horizontal guides for peers in the next ELK column.
        proximityMarginY: (dragging)=>Math.max(dragging.width, NODE_WIDTH)
    });
    const { screenToFlowPosition } = useReactFlow();
    const { nodes: decoratedNodes, edges: assembledEdges, onNodesChange, onNodeDragStart, onNodeDrag, onNodeDragStop, edgeWaypointManager, isSeeding } = useEditableFlow({
        adapter,
        source,
        savedPositions: nodePositions,
        laneLayout: PROCESS_FLOW_LANE_LAYOUT,
        decorate,
        // First-mount migration: run ELK and persist positions for nodes that
        // arrive without a saved entry.
        seedAlgoOnReady: true,
        onDragEnd: handleDragEnd,
        transformNodeChanges: transformChanges,
        savedEdgeWaypoints: editableEdgesEnabled ? edgeWaypoints : undefined,
        onUpdateEdgeLayouts: editableEdgesEnabled ? onUpdateEdgeLayouts : undefined
    });
    const edges = useMemo(()=>{
        if (assembledEdges.length === 0) return EMPTY_EDGES;
        if (!editableEdgesEnabled) {
            return assembledEdges.map((edge)=>({
                    ...edge,
                    data: {
                        ...edge.data ?? {},
                        mode: "edit"
                    }
                }));
        }
        return assembledEdges.map((edge)=>{
            const key = makeEdgeKey(edge.source, edge.target, edge.sourceHandle ?? null);
            // Side-effect inside useMemo: the manager's meta map is hidden state
            // we need populated for `commitChange` to emit updates with full
            // identity. Cheap and idempotent.
            edgeWaypointManager.registerMeta(key, {
                sourceNodeId: edge.source,
                targetNodeId: edge.target,
                sourceHandleId: edge.sourceHandle ?? null
            });
            // Read from the saved map first (drives re-memo on identity change),
            // then fall back to the manager for keys seeded mid-session.
            const waypoints = edgeWaypoints?.get(key) ?? edgeWaypointManager.resolve(key);
            return {
                ...edge,
                data: {
                    ...edge.data ?? {},
                    mode: "edit",
                    waypoints
                }
            };
        });
    }, [
        assembledEdges,
        edgeWaypointManager,
        edgeWaypoints,
        editableEdgesEnabled
    ]);
    const waypointActionsRef = useRef({
        onCommit: ()=>{}
    });
    waypointActionsRef.current = {
        onCommit: (sourceId, targetId, sourceHandle, waypoints)=>{
            const key = makeEdgeKey(sourceId, targetId, sourceHandle);
            edgeWaypointManager.registerMeta(key, {
                sourceNodeId: sourceId,
                targetNodeId: targetId,
                sourceHandleId: sourceHandle
            });
            edgeWaypointManager.commitChange(key, waypoints);
        }
    };
    const edgeCallbacksRef = useRef({
        onEdgeContextMenu: ()=>{},
        onEdgeClick: ()=>{}
    });
    edgeCallbacksRef.current = {
        onEdgeContextMenu: (source, target, sourceHandle, event)=>{
            onEdgeContextMenu?.(event, {
                id: `${source}->${target}`,
                source,
                target,
                sourceHandle
            });
        },
        onEdgeClick: (source, target, sourceHandle, clientX, clientY)=>{
            onEdgeContextMenu?.(new MouseEvent("contextmenu", {
                clientX,
                clientY
            }), {
                id: `${source}->${target}`,
                source,
                target,
                sourceHandle
            });
            const el = document.elementFromPoint(clientX, clientY);
            el?.dispatchEvent(new MouseEvent("contextmenu", {
                bubbles: true,
                cancelable: true,
                clientX,
                clientY
            }));
        }
    };
    // `data.isSelected` is our visual flag (read by node components); xyflow
    // owns `node.selected`. Preserve refs for unchanged nodes so `measured`
    // stays stable across renders.
    const displayNodes = useMemo(()=>decoratedNodes.map((node)=>{
            const isSelected = selectedNodeIds.has(node.id);
            return node.data?.isSelected === isSelected ? node : {
                ...node,
                data: {
                    ...node.data,
                    isSelected
                }
            };
        }), [
        decoratedNodes,
        selectedNodeIds
    ]);
    // --- Sticky notes (ADR 0008) -------------------------------------------
    // The note layer owns everything note-specific — built nodes/connectors, the
    // transient geometry override, link mode — and wraps the base canvas handlers
    // so the editor just composes a layer (no note id prefixes, geometry override
    // or link mode in this body). See `useNoteLayer`.
    const noteBaseHandlers = useMemo(()=>({
            onNodesChange,
            onNodeDrag,
            onNodeDragStop,
            onNodeDragStart,
            onNodeClick: handleNodeClick,
            onPaneClick: handlePaneClick
        }), [
        onNodesChange,
        onNodeDrag,
        onNodeDragStop,
        onNodeDragStart,
        handleNodeClick,
        handlePaneClick
    ]);
    const noteLayer = useNoteLayer({
        notes,
        selectedNodeIds,
        displayNodes,
        onUpdateNote,
        onPlaceNote,
        onRetargetNote,
        onDeleteNote,
        // Notes live outside the controlled canvas state, so their marquee/click
        // `select` changes can't land via `applyNodeChanges` — route them into the
        // selection model directly.
        onNoteSelectChanges: applyExternalSelectChanges,
        base: noteBaseHandlers,
        clearGuides
    });
    const layoutNodes = useMemo(()=>noteLayer.noteNodes.length ? [
            ...displayNodes,
            ...noteLayer.noteNodes
        ] : displayNodes, [
        displayNodes,
        noteLayer.noteNodes
    ]);
    const detailPanelReveal = useDetailPanelReveal({
        selectedNodeId,
        revealRequest
    });
    const placeholderLayer = useEmptyStepPlaceholderLayer({
        process,
        openAssignments,
        nodes: layoutNodes,
        onAddFirstStep,
        revealNextSelection: detailPanelReveal.revealNextSelection
    });
    const allNodes = useMemo(()=>placeholderLayer.nodes.length ? [
            ...layoutNodes,
            ...placeholderLayer.nodes
        ] : layoutNodes, [
        layoutNodes,
        placeholderLayer.nodes
    ]);
    const allEdges = useMemo(()=>noteLayer.noteEdges.length || placeholderLayer.edges.length ? [
            ...edges,
            ...noteLayer.noteEdges,
            ...placeholderLayer.edges
        ] : edges, [
        edges,
        noteLayer.noteEdges,
        placeholderLayer.edges
    ]);
    // Backspace/Delete on selected nodes: notes route to their own delete
    // handler (undoable via server history); backing-entity nodes go to
    // `onDeleteNodes` partitioned by `canvasKind`.
    const handleNodesDelete = useCallback((deleted)=>{
        if (onDeleteNote) {
            for (const node of deleted){
                if (isNoteNodeId(node.id)) onDeleteNote(noteIdFromNodeId(node.id));
            }
        }
        if (!onDeleteNodes) return;
        const nodes = deleted.filter((node)=>isBackingEntityKind(classifyCanvasNode(node))).map((node)=>({
                nodeId: node.id,
                kind: classifyCanvasNode(node),
                eventId: node.data?.eventId
            }));
        if (nodes.length) onDeleteNodes(nodes);
    }, [
        onDeleteNodes,
        onDeleteNote
    ]);
    // Flip the selection gate before RF's post-mousedown `onSelectionChange`
    // commits, then chain into the note-aware structural drag-start (a no-op for
    // note nodes, which sit outside the lane lifecycle).
    const handleNodeDragStart = useCallback((event, node, nodes)=>{
        selectionOnDragStart();
        noteLayer.handlers.onNodeDragStart(event, node, nodes);
    }, [
        selectionOnDragStart,
        noteLayer.handlers
    ]);
    // Rewrite the drag-end effects each render so they close over the current
    // `onNodesChange` / `clearGuides`. The selection hook's stable
    // `handleDragEnd` reads from an internal ref that `registerDragEndEffects`
    // updates here.
    registerDragEndEffects({
        onNodesChange,
        clearGuides
    });
    useReleaseStuckModifiers();
    const isPanMode = cursorMode !== "select";
    const canvasClassName = isPanMode ? "cursor-grab active:cursor-grabbing" : "cursor-default";
    const handlePaneContextMenu = useCallback((event)=>{
        if (!onPaneContextMenu) return;
        const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY
        });
        onPaneContextMenu(event, position);
    }, [
        onPaneContextMenu,
        screenToFlowPosition
    ]);
    const handleNodeContextMenu = useCallback((event, node)=>{
        // Notes have no node context menu (the target resolves to null downstream
        // and the menu is suppressed). Select on right-click so the inline
        // toolbar (recolour/delete) surfaces, matching left-click.
        if (isNoteNodeId(node.id)) noteLayer.handlers.onNoteContextMenuSelect(event, node);
        if (!onNodeContextMenu) return;
        onNodeContextMenu(event, node);
    }, [
        onNodeContextMenu,
        noteLayer.handlers
    ]);
    const handleEdgeContextMenu = useCallback((event, edge)=>{
        if (!onEdgeContextMenu) return;
        onEdgeContextMenu(event, edge);
    }, [
        onEdgeContextMenu
    ]);
    // `displayNodes` is `decoratedNodes` with only `data.isSelected` overlaid —
    // same ids, kinds and payloads — so one node source answers both the event
    // and the end-node lookup.
    const selection = useMemo(()=>resolveSelection({
            selectedNodeId,
            nodes: displayNodes,
            steps: process?.processStepRecordings,
            openAssignments
        }), [
        selectedNodeId,
        displayNodes,
        process,
        openAssignments
    ]);
    // Resolved before the panel is rendered: a selection the renderer has nothing
    // to show for (a legacy event input whose id matches no event row) must not
    // put empty chrome with a close button on the canvas.
    const detailContent = selection.kind === "none" || !renderDetail ? null : renderDetail(selection, closeDetailPanel);
    const editorActions = useMemo(()=>({
            onConnectSteps,
            onDisconnectSteps,
            onSavePositions
        }), [
        onConnectSteps,
        onDisconnectSteps,
        onSavePositions
    ]);
    // Hide the canvas until the first-mount ELK seeding pass settles — otherwise
    // sentinel-positioned nodes would paint at (0,0) for one frame and visibly
    // jump to their computed positions. A blank placeholder keeps the layout
    // stable during that window. `isSeeding` is always `false` after the first
    // load (positions are persisted), so subsequent mounts render immediately.
    const canvas = isSeeding ? /*#__PURE__*/ _jsx("div", {
        className: "w-full h-full"
    }) : /*#__PURE__*/ _jsx(EdgeCallbacksContext.Provider, {
        value: edgeCallbacksRef,
        children: /*#__PURE__*/ _jsx(EdgeWaypointActionsContext.Provider, {
            value: editableEdgesEnabled ? waypointActionsRef : null,
            children: /*#__PURE__*/ _jsxs(FlowCanvas, {
                adapter: adapter,
                nodes: allNodes,
                edges: allEdges,
                onNodesChange: noteLayer.handlers.onNodesChange,
                onNodeDragStart: handleNodeDragStart,
                onNodeDrag: noteLayer.handlers.onNodeDrag,
                onNodeDragStop: noteLayer.handlers.onNodeDragStop,
                onNodeClick: noteLayer.handlers.onNodeClick,
                onPaneClick: noteLayer.handlers.onPaneClick,
                onSelectionChange: handleSelectionChange,
                onSelectionStart: handleSelectionStart,
                onSelectionEnd: handleSelectionEnd,
                onNodesDelete: handleNodesDelete,
                onConnect: handleConnect,
                onConnectStart: handleConnectStart,
                onConnectEnd: handleConnectEnd,
                onPaneContextMenu: handlePaneContextMenu,
                onNodeContextMenu: handleNodeContextMenu,
                onEdgeContextMenu: handleEdgeContextMenu,
                fitView: true,
                fitViewOptions: INITIAL_FIT_VIEW_OPTIONS,
                minZoom: 0.1,
                maxZoom: 2,
                proOptions: {
                    hideAttribution: true
                },
                connectionMode: ConnectionMode.Strict,
                connectionLineComponent: SnapConnectionLine,
                panOnDrag: isPanMode ? true : PAN_ON_MIDDLE_AND_RIGHT,
                selectionOnDrag: !isPanMode,
                selectionMode: SelectionMode.Partial,
                multiSelectionKeyCode: "Shift",
                panActivationKeyCode: "Space",
                deleteKeyCode: [
                    "Backspace",
                    "Delete"
                ],
                nodesDraggable: true,
                className: canvasClassName,
                children: [
                    /*#__PURE__*/ _jsx(FlowViewportHandler, {
                        fitRequest: fitRequest,
                        layoutReady: decoratedNodes.length > 0
                    }),
                    /*#__PURE__*/ _jsx(Background, {}),
                    helperLinesOverlay,
                    noteLayer.linkingNoteId && /*#__PURE__*/ _jsx(Panel, {
                        position: "top-center",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "rounded-full bg-info px-3 py-1 text-xs font-medium text-white shadow",
                            children: t("processFlowEditor.noteLinkHint")
                        })
                    }),
                    bottomToolbar && /*#__PURE__*/ _jsx(Panel, {
                        position: "bottom-center",
                        children: bottomToolbar
                    }),
                    detailContent ? /*#__PURE__*/ _jsx(FloatingDetailPanel, {
                        panelRef: detailPanelReveal.detailPanelRef,
                        children: detailContent
                    }) : !renderDetail && selection.kind === "step" ? /*#__PURE__*/ _jsx(FloatingDetailPanel, {
                        panelRef: detailPanelReveal.detailPanelRef,
                        children: /*#__PURE__*/ _jsx(ProcessFlowStepDetailSidebar, {
                            step: selection.step,
                            onClose: closeDetailPanel
                        })
                    }) : null
                ]
            })
        })
    });
    return /*#__PURE__*/ _jsx(EditorModeContext.Provider, {
        value: true,
        children: /*#__PURE__*/ _jsx(EditorActionsContext.Provider, {
            value: editorActions,
            children: /*#__PURE__*/ _jsx(EditorSelectionContext.Provider, {
                value: selectedNodeIds,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "flex flex-col h-full",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "flex-1",
                        children: contextMenu ? contextMenu(canvas) : canvas
                    })
                })
            })
        })
    });
}

//# sourceMappingURL=ProcessFlowEditor.js.map